import React, { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Clock, Plus, Users, Building2 } from 'lucide-react';
import { toast } from 'sonner';

interface DefaultTask {
  id: string;
  name: string;
  description: string | null;
  due_in_days: number;
  source: 'organization' | 'team';
  created_at: string;
}

interface TaskCreationDialogProps {
  onTaskSelect: (task: DefaultTask) => void;
  onCustomTaskCreate: (title: string, description: string, dueDate: string) => void;
  customerId: string;
  customerName: string;
}

const TaskCreationDialog: React.FC<TaskCreationDialogProps> = ({
  onTaskSelect,
  onCustomTaskCreate,
  customerId,
  customerName
}) => {
  const { user } = useAuth();
  const [defaultTasks, setDefaultTasks] = useState<DefaultTask[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [userProfile, setUserProfile] = useState<{ organization_id: string | null; team_id: string | null } | null>(null);
  
  // Custom task state
  const [customTitle, setCustomTitle] = useState('');
  const [customDescription, setCustomDescription] = useState('');
  const [customDueDate, setCustomDueDate] = useState('');

  useEffect(() => {
    fetchUserProfile();
  }, [user]);

  useEffect(() => {
    if (userProfile) {
      fetchAllTasks();
    }
  }, [userProfile]);

  useEffect(() => {
    // Set default due date to tomorrow
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setCustomDueDate(tomorrow.toISOString().split('T')[0]);
  }, []);

  const fetchUserProfile = async () => {
    if (!user) return;

    try {
      const { data: profile, error } = await supabase
        .from('profiles')
        .select('organization_id, team_id')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      setUserProfile(profile);
    } catch (error) {
      console.error('Error fetching user profile:', error);
    }
  };

  const fetchAllTasks = async () => {
    if (!userProfile) return;

    setIsLoading(true);
    try {
      const allTasks: DefaultTask[] = [];

      // Fetch team tasks FIRST (team tasks have priority)
      if (userProfile.team_id) {
        const { data: teamTasks, error: teamError } = await supabase
          .from('default_team_tasks')
          .select('id, name, description, due_in_days, created_at')
          .eq('team_id', userProfile.team_id)
          .order('due_in_days', { ascending: true });

        if (teamError) throw teamError;
        
        if (teamTasks) {
          allTasks.push(...teamTasks.map(task => ({
            id: task.id,
            name: task.name,
            description: task.description,
            due_in_days: task.due_in_days || 7,
            source: 'team' as const,
            created_at: task.created_at
          })));
        }
      }

      // Fetch organization tasks
      if (userProfile.organization_id) {
        const { data: orgTasks, error: orgError } = await supabase
          .from('default_organization_tasks')
          .select('id, name, description, due_in_days, created_at')
          .eq('organization_id', userProfile.organization_id)
          .order('due_in_days', { ascending: true });

        if (orgError) throw orgError;
        
        if (orgTasks) {
          allTasks.push(...orgTasks.map(task => ({
            id: task.id,
            name: task.name,
            description: task.description,
            due_in_days: task.due_in_days,
            source: 'organization' as const,
            created_at: task.created_at
          })));
        }
      }

      setDefaultTasks(allTasks);
    } catch (error) {
      console.error('Error fetching tasks:', error);
      toast.error('Fehler beim Laden der Aufgaben');
    } finally {
      setIsLoading(false);
    }
  };

  const getDueDaysLabel = (days: number) => {
    if (days === 1) return '1 Tag';
    if (days === 7) return '1 Woche';
    if (days === 14) return '2 Wochen';
    if (days === 30) return '1 Monat';
    return `${days} Tage`;
  };

  const getDueDaysColor = (days: number) => {
    if (days === 1) return 'bg-red-100 text-red-700';
    if (days <= 7) return 'bg-orange-100 text-orange-700';
    if (days <= 30) return 'bg-blue-100 text-blue-700';
    return 'bg-green-100 text-green-700';
  };

  const handleTaskSelection = (task: DefaultTask) => {
    onTaskSelect(task);
    toast.success(`Aufgabe "${task.name}" ausgewählt`);
  };

  const handleCustomTaskCreate = () => {
    if (!customTitle.trim()) {
      toast.error('Bitte geben Sie einen Titel ein');
      return;
    }

    onCustomTaskCreate(customTitle, customDescription, customDueDate);
    
    // Reset form
    setCustomTitle('');
    setCustomDescription('');
    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    setCustomDueDate(tomorrow.toISOString().split('T')[0]);
  };

  const teamTasks = defaultTasks.filter(t => t.source === 'team');
  const orgTasks = defaultTasks.filter(t => t.source === 'organization');

  return (
    <div className="w-full overflow-hidden">
      <Tabs defaultValue="standard" className="w-full">
      <TabsList className="grid w-full grid-cols-2">
        <TabsTrigger value="standard">Aufgaben</TabsTrigger>
        <TabsTrigger value="custom">Eigene Aufgabe</TabsTrigger>
      </TabsList>
      
      <TabsContent value="standard" className="mt-4">
        <div className="overflow-y-auto max-h-[50vh] pr-2">
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : defaultTasks.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Keine Aufgaben verfügbar</p>
              <p className="text-sm">Bitten Sie Ihren Teamleiter oder Administrator, Aufgaben zu erstellen</p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Team Tasks Section */}
              {teamTasks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Users className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-medium text-sm text-muted-foreground">Team-Aufgaben</h3>
                  </div>
                  <div className="space-y-2">
                    {teamTasks.map((task) => (
                      <Card 
                        key={task.id} 
                        className="cursor-pointer hover:border-primary transition-colors"
                        onClick={() => handleTaskSelection(task)}
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm sm:text-base truncate break-words">{task.name}</h4>
                                <Badge className={`${getDueDaysColor(task.due_in_days)} flex-shrink-0 self-start sm:self-auto`}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  {getDueDaysLabel(task.due_in_days)}
                                </Badge>
                              </div>
                              {task.description && (
                                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 break-words">
                                  {task.description}
                                </p>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="flex-shrink-0 self-start sm:self-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskSelection(task);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}

              {/* Organization Tasks Section */}
              {orgTasks.length > 0 && (
                <div>
                  <div className="flex items-center gap-2 mb-3">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                    <h3 className="font-medium text-sm text-muted-foreground">Organisations-Aufgaben</h3>
                  </div>
                  <div className="space-y-2">
                    {orgTasks.map((task) => (
                      <Card 
                        key={task.id} 
                        className="cursor-pointer hover:border-primary transition-colors"
                        onClick={() => handleTaskSelection(task)}
                      >
                        <CardContent className="p-3 sm:p-4">
                          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                            <div className="flex-1 min-w-0 overflow-hidden">
                              <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-1">
                                <h4 className="font-medium text-sm sm:text-base truncate break-words">{task.name}</h4>
                                <Badge className={`${getDueDaysColor(task.due_in_days)} flex-shrink-0 self-start sm:self-auto`}>
                                  <Clock className="h-3 w-3 mr-1" />
                                  {getDueDaysLabel(task.due_in_days)}
                                </Badge>
                              </div>
                              {task.description && (
                                <p className="text-xs sm:text-sm text-muted-foreground line-clamp-2 break-words">
                                  {task.description}
                                </p>
                              )}
                            </div>
                            <Button 
                              size="sm" 
                              variant="ghost"
                              className="flex-shrink-0 self-start sm:self-auto"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleTaskSelection(task);
                              }}
                            >
                              <Plus className="h-4 w-4" />
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </TabsContent>
      
      <TabsContent value="custom" className="mt-4">
        <div className="space-y-4">
          <div>
            <Label htmlFor="custom-title">Titel</Label>
            <Input
              id="custom-title"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Was soll erledigt werden?"
            />
          </div>
          
          <div>
            <Label htmlFor="custom-description">Beschreibung (optional)</Label>
            <Textarea
              id="custom-description"
              value={customDescription}
              onChange={(e) => setCustomDescription(e.target.value)}
              placeholder="Zusätzliche Details zur Aufgabe..."
              rows={3}
            />
          </div>
          
          <div>
            <Label htmlFor="custom-due-date">Fälligkeitsdatum</Label>
            <Input
              id="custom-due-date"
              type="date"
              value={customDueDate}
              onChange={(e) => setCustomDueDate(e.target.value)}
            />
          </div>
          
           <Button 
             onClick={handleCustomTaskCreate}
             className="w-full"
             disabled={!customTitle.trim()}
           >
             <Plus className="h-4 w-4 mr-2" />
             <span className="truncate">To-Do für {customerName} erstellen</span>
           </Button>
        </div>
      </TabsContent>
    </Tabs>
    </div>
  );
};

export default TaskCreationDialog;
